import { useState, useMemo } from "react";
import axios from "axios";
import { Phone, MessageSquare, Mail, CalendarCheck, Coffee, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { trackConversion } from "@/lib/analytics";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_EMBED = process.env.REACT_APP_BOOKING_EMBED_URL;

const inputCls = "rounded-2xl border-2 border-white/70 bg-white/10 px-4 py-3 font-semibold text-white placeholder:text-white/60 focus:outline-none focus:ring-4 focus:ring-white/30";

export default function Booking({ selectedUnit, suburb, photoId }) {
  const embedSrc = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedUnit) params.set("unit_id", selectedUnit.id);
    if (photoId) params.set("photo_url", `${process.env.REACT_APP_BACKEND_URL}/api/lead-photos/${photoId}`);
    const q = params.toString();
    return q ? `${BOOKING_EMBED}?${q}` : BOOKING_EMBED;
  }, [selectedUnit, photoId]);
  const [form, setForm] = useState({ name: "", phone: "", suburb: suburb?.name || "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, unit_id: selectedUnit?.id || null });
      trackConversion("callback_submit");
      toast.success("Got it! We'll call you back shortly to lock in your swap.");
      setForm({ name: "", phone: "", suburb: suburb?.name || "", message: "" });
    } catch {
      toast.error("That didn't send — call us on 0475 776 555 instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="book" className="bg-tpeco-blue text-white border-y-4 border-tpeco-ink py-20 sm:py-28 grain-overlay" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-y-0">
        <div className="lg:col-start-1 lg:row-start-1">
          <Reveal>
            <span className="chip-brutal bg-white border-white text-tpeco-blue">CHAPTER 03 — LOCK IT IN</span>
            <h2 className="mt-4 font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95]" data-testid="booking-heading">
              PICK YOUR SLOT.<br /><span className="bg-tpeco-pink px-3 rounded-2xl inline-block">WE BRING THE HEAT.</span>
            </h2>
            <p className="mt-4 max-w-lg font-semibold text-white/85">
              Australia's first online booking portal for tradies — see live availability and grab a time that suits.
              Same or next day across Perth, Rockingham to Joondalup.
            </p>
          </Reveal>

          {selectedUnit && (
            <Reveal i={1} className="mt-6 rounded-3xl border-[3px] border-white bg-white/10 p-4 flex items-center gap-4" data-testid="selected-unit-summary">
              <img src={selectedUnit.img} alt={selectedUnit.model} className="w-16 h-16 lg:w-40 lg:h-40 object-contain bg-white rounded-2xl border-2 border-tpeco-ink" />
              <div>
                <span className="text-xs font-extrabold uppercase tracking-widest text-tpeco-pink">YOU PICKED · {selectedUnit.id}</span>
                <p className="font-display text-2xl uppercase leading-tight">{selectedUnit.brand} {selectedUnit.model}</p>
                <p className="font-bold text-sm">${selectedUnit.price.toLocaleString()} installed — mention {selectedUnit.id} when booking</p>
              </div>
            </Reveal>
          )}
        </div>

        <Reveal i={1} className="lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-28 self-start w-full">
          <div className="rounded-[2rem] border-[3px] border-white bg-white text-tpeco-ink shadow-[0_10px_0_0_#0A0A0A] overflow-hidden">
            <div className="flex items-center justify-between gap-2 bg-tpeco-pink text-white px-4 py-3">
              <span className="font-display uppercase text-xl flex items-center gap-2"><CalendarCheck className="w-5 h-5" /> LIVE AVAILABILITY</span>
              <span className="chip-brutal bg-tpeco-blue border-white text-white !text-xs">BOOK A REAL SLOT ↓</span>
            </div>
            <iframe
              src={embedSrc}
              title="Live booking portal — The Plumbing & Electrical Co."
              className="w-full h-[640px] bg-white"
              loading="lazy"
              data-testid="booking-iframe"
            />
            <div className="border-t-2 border-tpeco-ink/10 px-4 py-3 text-center text-xs font-bold uppercase tracking-widest text-neutral-500">
              Portal not loading? <a href="tel:0475776555" className="text-tpeco-blue underline underline-offset-2" data-testid="iframe-fallback-call">Call 0475 776 555</a> and we'll book you in.
            </div>
          </div>
        </Reveal>

        <div className="lg:col-start-1 lg:row-start-2">
          <Reveal i={2} className="lg:mt-8 grid sm:grid-cols-2 gap-3">
            <a href="tel:0475776555" className="btn-brutal w-full" data-testid="booking-call-mobile"><Phone className="w-5 h-5" /> CALL 0475 776 555</a>
            <a href="sms:0475776555" className="btn-brutal-light w-full" data-testid="booking-text-button"><MessageSquare className="w-5 h-5" /> TEXT US A PHOTO</a>
            <a href="tel:+61894773848" className="btn-brutal-light w-full" data-testid="booking-call-landline"><Phone className="w-5 h-5" /> (08) 9477 3848</a>
            <a href="mailto:team@tpeco.com.au" className="btn-brutal-light w-full !text-lg" data-testid="booking-email-button"><Mail className="w-5 h-5" /> TEAM@TPECO.COM.AU</a>
          </Reveal>

          <Reveal i={3} className="mt-8 rounded-3xl border-2 border-dashed border-white/60 p-4 flex items-center gap-3" data-testid="coffee-promo">
            <Coffee className="w-8 h-8 text-tpeco-pink shrink-0" />
            <p className="text-sm font-semibold text-white/85">
              <span className="text-tpeco-pink font-extrabold uppercase">Coffee fix:</span> book the first 7am slot of the day
              online and we'll bring a free coffee from a local café to your door. Yum.
            </p>
          </Reveal>

          <Reveal i={4} className="mt-8" id="enquiry-form">
            <h3 className="font-display uppercase text-2xl">PREFER A CALLBACK?</h3>
            <form onSubmit={submit} className="mt-4 grid gap-3" data-testid="callback-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name" data-testid="callback-name-input" className={inputCls} />
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Mobile number" data-testid="callback-phone-input" className={inputCls} />
              </div>
              <input value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })}
                placeholder="Suburb" data-testid="callback-suburb-input" className={inputCls} />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2}
                placeholder={selectedUnit ? `I'd like the ${selectedUnit.id} swap (${selectedUnit.brand} ${selectedUnit.model})` : "Anything we should know? (unit type, access, urgency)"}
                data-testid="callback-message-input" className={inputCls} />
              <button type="submit" disabled={sending} className="btn-brutal w-full sm:w-auto" data-testid="callback-submit-button">
                {sending ? "SENDING…" : "REQUEST CALLBACK"} <ArrowRight className="w-5 h-5" />
              </button>
              <p className="text-xs font-semibold text-white/70">We'll ping the team at team@tpeco.com.au the second you hit send.</p>
              <p className="mt-1 text-sm font-bold text-white bg-white/10 border-2 border-dashed border-tpeco-pink rounded-2xl px-4 py-3" data-testid="callback-price-beat-policy">
                <span className="text-tpeco-pink font-extrabold uppercase">Our policy will always stand,</span> Show us a genuine written quote for the same unit and install — we'll beat it!
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
